export interface NeighborhoodBusiness {
  id: number;
  name: string;
  type: string;
  category: string;
  description: string;
  address: string;
  phone: string;
  website: string;
  image: string;
  icon: string;
  rating: number;
  reviews: number;
  priceRange: string;
  hours: string;
  openNow: boolean;
  verified: boolean;
  featured: boolean;
  offer: string;
  highlights: string[];
}

export interface NeighborhoodDetail {
  id: number;
  slug: string;
  name: string;
  tagline: string;
  description: string;
  longDescription: string;
  accentColor: string;
  heroImage: string;
  businessCount: number;
  walkScore: number;
  transitScore: number;
  diningCount: number;
  avgRating: number;
  totalReviews: number;
  mapQuery: string;
  highlights: string[];
  categories: string[];
  businesses: NeighborhoodBusiness[];
  nearbyAttractions: { name: string; distance: string; icon: string }[];
  parkingInfo: string;
  transitInfo: string;
}

export const neighborhoodDetails: NeighborhoodDetail[] = [
  {
    id: 1,
    slug: 'historic-old-town',
    name: 'Historic Old Town',
    tagline: 'Arts & Culture Hub',
    description: 'The heart of Roseville with charming boutiques, craft breweries, antique shops, and a thriving arts scene along Vernon Street and the historic railroad district.',
    longDescription: 'Historic Old Town Roseville is the cultural heartbeat of the city, where Victorian-era charm meets modern creativity. Stroll along Vernon Street to discover independent art galleries, craft breweries, antique shops, and farm-to-table restaurants. The district hosts regular events including the popular Thursday Night Market, art walks, and live music performances. With its tree-lined sidewalks, string-lit patios, and welcoming community vibe, Old Town is where locals and visitors alike come to experience authentic Roseville.',
    accentColor: '#E07A5F',
    heroImage: 'https://public.readdy.ai/ai/img_res/eabc89b38e163c4322297cae866f2d48.jpg',
    businessCount: 85,
    walkScore: 88,
    transitScore: 72,
    diningCount: 34,
    avgRating: 4.7,
    totalReviews: 4280,
    mapQuery: 'Historic+Old+Town+Roseville+CA',
    highlights: ['Art Galleries', 'Craft Breweries', 'Antique Shops', 'Live Music', 'Thursday Night Market', 'Farm-to-Table Dining'],
    categories: ['dining', 'shopping', 'entertainment', 'services'],
    nearbyAttractions: [
      { name: 'Roseville Amtrak Station', distance: '0.2 mi', icon: 'ri-train-line' },
      { name: 'Royer Park', distance: '0.5 mi', icon: 'ri-tree-line' },
      { name: 'Carnegie Museum', distance: '0.3 mi', icon: 'ri-building-line' },
    ],
    parkingInfo: 'Free street parking available along Vernon St and surrounding blocks. Public parking lot at 401 Vernon St with 120 spaces.',
    transitInfo: 'Roseville Transit Route 1 and 3 serve Old Town. Amtrak Capitol Corridor station is a 3-minute walk.',
    businesses: [
      {
        id: 101,
        name: 'Monk\\\'s Cellar Brewery',
        type: 'Brewery & Restaurant',
        category: 'dining',
        description: 'Award-winning craft brewery housed in a beautifully restored 1940s building. Features rotating seasonal taps, wood-fired pizzas, and a spacious beer garden with live music on weekends.',
        address: '340 Vernon St, Roseville, CA 95678',
        phone: '(916) 555-2001',
        website: 'https://monkscellar.com',
        image: 'https://readdy.ai/api/search-image?query=Craft%20brewery%20interior%20with%20industrial%20chic%20decor%2C%20copper%20brewing%20tanks%20visible%20through%20glass%20wall%2C%20wooden%20bar%20with%20beer%20taps%2C%20warm%20Edison%20bulb%20lighting%2C%20exposed%20brick%20walls%2C%20patrons%20enjoying%20craft%20beer%2C%20cozy%20inviting%20atmosphere&width=500&height=350&seq=nbbus101&orientation=landscape',
        icon: 'ri-goblet-line',
        rating: 4.7,
        reviews: 892,
        priceRange: '$$',
        hours: 'Mon-Thu 11AM-10PM, Fri-Sat 11AM-12AM, Sun 10AM-9PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: 'Happy Hour 3-6PM Daily',
        highlights: ['Beer Garden', 'Live Music', 'Wood-Fired Pizza', 'Dog Friendly'],
      },
      {
        id: 102,
        name: 'Blue Line Arts Gallery',
        type: 'Art Gallery & Studio',
        category: 'entertainment',
        description: 'Contemporary art gallery showcasing works by local and regional artists. Monthly exhibitions, artist talks, workshops, and a curated gift shop featuring handmade jewelry and prints.',
        address: '405 Vernon St Suite 100, Roseville, CA 95678',
        phone: '(916) 555-2002',
        website: 'https://bluelinearts.org',
        image: 'https://readdy.ai/api/search-image?query=Modern%20art%20gallery%20interior%20with%20white%20walls%20displaying%20colorful%20contemporary%20paintings%20and%20sculptures%2C%20track%20lighting%2C%20polished%20concrete%20floors%2C%20visitors%20admiring%20artwork%2C%20clean%20minimalist%20gallery%20space%20with%20high%20ceilings&width=500&height=350&seq=nbbus102&orientation=landscape',
        icon: 'ri-palette-line',
        rating: 4.9,
        reviews: 234,
        priceRange: '$',
        hours: 'Tue-Sat 10AM-5PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: 'Free First Friday Art Walk',
        highlights: ['Monthly Exhibitions', 'Art Workshops', 'Gift Shop', 'Artist Talks'],
      },
      {
        id: 103,
        name: 'The Rustic Table',
        type: 'Farm-to-Table Restaurant',
        category: 'dining',
        description: 'Seasonal California cuisine featuring locally sourced ingredients from Placer County farms. Beautiful garden patio, craft cocktails, and an award-winning weekend brunch.',
        address: '320 Lincoln St, Roseville, CA 95678',
        phone: '(916) 555-2003',
        website: 'https://rustictableroseville.com',
        image: 'https://readdy.ai/api/search-image?query=Charming%20farm%20to%20table%20restaurant%20with%20rustic%20wooden%20tables%2C%20fresh%20herb%20centerpieces%2C%20exposed%20beam%20ceiling%2C%20warm%20Edison%20bulb%20lighting%2C%20open%20kitchen%20concept%2C%20beautiful%20plated%20dishes%2C%20California%20casual%20fine%20dining%20atmosphere&width=500&height=350&seq=nbbus103&orientation=landscape',
        icon: 'ri-restaurant-line',
        rating: 4.8,
        reviews: 298,
        priceRange: '$$$',
        hours: 'Tue-Sun 11AM-10PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: 'Prix Fixe Dinner $45',
        highlights: ['Garden Patio', 'Craft Cocktails', 'Weekend Brunch', 'Locally Sourced'],
      },
      {
        id: 104,
        name: 'Roseville Pottery Studio',
        type: 'Artisan Shop & Studio',
        category: 'shopping',
        description: 'Handcrafted pottery and ceramics studio offering unique pieces for home and garden. Drop-in pottery classes, private events, and a curated selection of local artisan goods.',
        address: '415 Vernon St, Roseville, CA 95678',
        phone: '(916) 555-2004',
        website: 'https://rosevillepottery.com',
        image: 'https://readdy.ai/api/search-image?query=Artisan%20pottery%20studio%20with%20handmade%20ceramic%20bowls%20and%20vases%20on%20wooden%20shelves%2C%20pottery%20wheel%20in%20background%2C%20warm%20natural%20lighting%2C%20earthy%20tones%2C%20creative%20workshop%20space%20with%20finished%20pieces%20displayed%20beautifully&width=500&height=350&seq=nbbus104&orientation=landscape',
        icon: 'ri-store-2-line',
        rating: 4.8,
        reviews: 156,
        priceRange: '$$',
        hours: 'Wed-Sun 10AM-6PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '15% Off First Class',
        highlights: ['Pottery Classes', 'Handmade Gifts', 'Private Events', 'Local Artisans'],
      },
      {
        id: 105,
        name: 'The Owl Club',
        type: 'Bar & Lounge',
        category: 'dining',
        description: 'Speakeasy-inspired cocktail lounge with craft cocktails, small plates, and a curated wine list. Intimate atmosphere with vintage decor and weekend DJ sets.',
        address: '305 Vernon St, Roseville, CA 95678',
        phone: '(916) 555-2005',
        website: 'https://owlclubroseville.com',
        image: 'https://readdy.ai/api/search-image?query=Elegant%20speakeasy%20style%20cocktail%20bar%20with%20dark%20wood%20paneling%2C%20vintage%20leather%20bar%20stools%2C%20ambient%20low%20lighting%2C%20craft%20cocktails%20on%20marble%20bar%20top%2C%20art%20deco%20inspired%20decor%2C%20sophisticated%20nightlife%20atmosphere&width=500&height=350&seq=nbbus105&orientation=landscape',
        icon: 'ri-goblet-line',
        rating: 4.5,
        reviews: 678,
        priceRange: '$$',
        hours: 'Wed-Sat 5PM-1AM',
        openNow: true,
        verified: false,
        featured: false,
        offer: '',
        highlights: ['Craft Cocktails', 'Live DJ', 'Small Plates', 'Vintage Decor'],
      },
      {
        id: 106,
        name: 'Craft & Cork Wine Bar',
        type: 'Wine Bar',
        category: 'dining',
        description: 'Upscale wine bar featuring an extensive selection of California wines, artisan cheese boards, and small plates. Live music on weekends and private event space.',
        address: '410 Vernon St, Roseville, CA 95678',
        phone: '(916) 555-2006',
        website: 'https://craftandcork.com',
        image: 'https://readdy.ai/api/search-image?query=Elegant%20wine%20bar%20interior%20with%20exposed%20brick%20walls%2C%20wine%20bottles%20displayed%20on%20wooden%20racks%2C%20ambient%20warm%20lighting%2C%20comfortable%20bar%20seating%2C%20sophisticated%20atmosphere%20with%20cheese%20board%20on%20counter%2C%20California%20wine%20country%20vibe&width=500&height=350&seq=nbbus106&orientation=landscape',
        icon: 'ri-goblet-line',
        rating: 4.8,
        reviews: 176,
        priceRange: '$$$',
        hours: 'Tue-Sun 4PM-11PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '',
        highlights: ['California Wines', 'Cheese Boards', 'Live Music', 'Private Events'],
      },
      {
        id: 107,
        name: 'Roseville Vintage Market',
        type: 'Vintage & Antiques',
        category: 'shopping',
        description: 'Curated vintage marketplace with furniture, clothing, vinyl records, and collectibles from over 40 local vendors. New arrivals weekly and seasonal pop-up events.',
        address: '225 Vernon St, Roseville, CA 95678',
        phone: '(916) 555-2007',
        website: 'https://rosevillevintage.com',
        image: 'https://readdy.ai/api/search-image?query=Eclectic%20vintage%20market%20interior%20with%20antique%20furniture%2C%20vinyl%20records%2C%20retro%20clothing%20racks%20and%20collectibles%20displayed%20artfully%2C%20warm%20lighting%2C%20exposed%20brick%20walls%2C%20charming%20bohemian%20shopping%20atmosphere%2C%20treasure%20hunting%20vibe&width=500&height=350&seq=nbbus107&orientation=landscape',
        icon: 'ri-store-3-line',
        rating: 4.6,
        reviews: 203,
        priceRange: '$',
        hours: 'Thu-Sun 10AM-6PM',
        openNow: true,
        verified: false,
        featured: false,
        offer: '',
        highlights: ['40+ Vendors', 'Vinyl Records', 'Vintage Clothing', 'Antique Furniture'],
      },
      {
        id: 108,
        name: 'Old Town Barber Co.',
        type: 'Barbershop',
        category: 'services',
        description: 'Classic barbershop with a modern twist. Traditional hot towel shaves, precision haircuts, and beard grooming in a relaxed, vintage-inspired atmosphere.',
        address: '350 Vernon St, Roseville, CA 95678',
        phone: '(916) 555-2008',
        website: 'https://oldtownbarber.com',
        image: 'https://readdy.ai/api/search-image?query=Classic%20barbershop%20interior%20with%20vintage%20barber%20chairs%2C%20large%20mirrors%2C%20warm%20wood%20paneling%2C%20traditional%20barber%20tools%20displayed%20neatly%2C%20retro%20decor%20with%20modern%20touches%2C%20welcoming%20masculine%20grooming%20space&width=500&height=350&seq=nbbus108&orientation=landscape',
        icon: 'ri-scissors-line',
        rating: 4.7,
        reviews: 312,
        priceRange: '$$',
        hours: 'Tue-Sat 9AM-7PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '$5 Off First Visit',
        highlights: ['Hot Towel Shaves', 'Walk-Ins Welcome', 'Beard Grooming', 'Classic Cuts'],
      },
    ],
  },
  {
    id: 2,
    slug: 'westfield-galleria',
    name: 'Westfield Galleria District',
    tagline: 'Premier Shopping & Dining',
    description: 'Northern California\\\'s premier shopping destination featuring 240+ stores, upscale dining, entertainment venues, and the iconic Westfield Galleria at Roseville.',
    longDescription: 'The Westfield Galleria District is Northern California\\\'s ultimate shopping and entertainment destination. Anchored by the massive Westfield Galleria at Roseville, this district features over 240 stores ranging from luxury brands to popular retailers, a diverse food court, sit-down restaurants, and a state-of-the-art movie theater. The surrounding area includes additional shopping plazas, hotels, and business centers, making it a complete lifestyle hub for residents and visitors.',
    accentColor: '#2D6A4F',
    heroImage: 'https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20modern%20upscale%20outdoor%20shopping%20mall%20with%20luxury%20retail%20stores%2C%20palm%20trees%2C%20clean%20walkways%2C%20shoppers%20enjoying%20sunny%20California%20day%2C%20contemporary%20architecture%20with%20glass%20facades%2C%20Northern%20California%20premier%20shopping%20center%2C%20wide%20angle%20cinematic%20photography&width=1200&height=500&seq=nbdetail002&orientation=landscape',
    businessCount: 240,
    walkScore: 76,
    transitScore: 68,
    diningCount: 52,
    avgRating: 4.4,
    totalReviews: 12500,
    mapQuery: 'Westfield+Galleria+Roseville+CA',
    highlights: ['240+ Stores', 'Fine Dining', 'Movie Theater', 'Luxury Brands', 'Food Court', 'Valet Parking'],
    categories: ['dining', 'shopping', 'entertainment', 'services'],
    nearbyAttractions: [
      { name: 'Hyatt Place Roseville', distance: '0.1 mi', icon: 'ri-hotel-line' },
      { name: 'Topgolf Roseville', distance: '0.8 mi', icon: 'ri-golf-ball-line' },
      { name: 'Fountains at Roseville', distance: '1.2 mi', icon: 'ri-drop-line' },
    ],
    parkingInfo: 'Free parking with over 6,000 spaces in covered and open-air lots. Valet parking available at main entrance.',
    transitInfo: 'Roseville Transit Routes 2 and 5 stop at the Galleria. Sacramento RT bus connections available.',
    businesses: [
      {
        id: 201,
        name: 'The Cheesecake Factory',
        type: 'Restaurant',
        category: 'dining',
        description: 'Iconic restaurant chain known for its extensive menu of over 250 items and legendary cheesecakes. Spacious dining room with elegant decor and a bustling bar area.',
        address: '1151 Galleria Blvd, Roseville, CA 95678',
        phone: '(916) 555-3001',
        website: 'https://thecheesecakefactory.com',
        image: 'https://readdy.ai/api/search-image?query=Upscale%20casual%20restaurant%20interior%20with%20elegant%20booth%20seating%2C%20warm%20ambient%20lighting%2C%20ornate%20ceiling%20details%2C%20large%20dining%20room%20with%20sophisticated%20decor%2C%20busy%20restaurant%20atmosphere%20with%20happy%20diners&width=500&height=350&seq=nbbus201&orientation=landscape',
        icon: 'ri-restaurant-line',
        rating: 4.3,
        reviews: 2341,
        priceRange: '$$',
        hours: 'Mon-Thu 11AM-10PM, Fri-Sat 11AM-11PM, Sun 10AM-10PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: '',
        highlights: ['250+ Menu Items', 'Famous Cheesecakes', 'Full Bar', 'Weekend Brunch'],
      },
      {
        id: 202,
        name: 'Nordstrom',
        type: 'Department Store',
        category: 'shopping',
        description: 'Premium department store offering designer fashion, shoes, beauty, and accessories. Known for exceptional customer service, personal stylists, and in-store alterations.',
        address: '1151 Galleria Blvd, Roseville, CA 95678',
        phone: '(916) 555-3002',
        website: 'https://nordstrom.com',
        image: 'https://readdy.ai/api/search-image?query=Elegant%20department%20store%20interior%20with%20luxury%20fashion%20displays%2C%20marble%20floors%2C%20modern%20lighting%20fixtures%2C%20designer%20clothing%20racks%2C%20upscale%20retail%20environment%20with%20sophisticated%20visual%20merchandising&width=500&height=350&seq=nbbus202&orientation=landscape',
        icon: 'ri-shopping-bag-line',
        rating: 4.5,
        reviews: 1876,
        priceRange: '$$$',
        hours: 'Mon-Sat 10AM-9PM, Sun 11AM-7PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: '',
        highlights: ['Personal Stylists', 'Alterations', 'Beauty Counter', 'Designer Brands'],
      },
      {
        id: 203,
        name: 'Century Theatres',
        type: 'Movie Theater',
        category: 'entertainment',
        description: 'State-of-the-art multiplex cinema with IMAX, XD, and luxury lounger seating. Full bar and dine-in options available in select auditoriums.',
        address: '1555 Eureka Rd, Roseville, CA 95678',
        phone: '(916) 555-3003',
        website: 'https://cinemark.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20movie%20theater%20lobby%20with%20neon%20lighting%2C%20large%20movie%20posters%2C%20concession%20stand%20with%20popcorn%20and%20drinks%2C%20sleek%20contemporary%20cinema%20design%2C%20comfortable%20luxury%20seating%20visible%20through%20glass%20doors&width=500&height=350&seq=nbbus203&orientation=landscape',
        icon: 'ri-film-line',
        rating: 4.4,
        reviews: 1234,
        priceRange: '$$',
        hours: 'Daily 10AM-12AM',
        openNow: true,
        verified: true,
        featured: false,
        offer: 'Discount Tuesdays $7.50',
        highlights: ['IMAX Screen', 'Luxury Loungers', 'Dine-In', 'Full Bar'],
      },
      {
        id: 204,
        name: 'Apple Store',
        type: 'Electronics',
        category: 'shopping',
        description: 'Official Apple retail store offering the latest iPhones, MacBooks, iPads, and accessories. Genius Bar for repairs and Today at Apple workshops.',
        address: '1151 Galleria Blvd, Roseville, CA 95678',
        phone: '(916) 555-3004',
        website: 'https://apple.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20Apple%20store%20interior%20with%20clean%20minimalist%20design%2C%20wooden%20display%20tables%20with%20latest%20devices%2C%20bright%20LED%20lighting%2C%20glass%20facade%2C%20customers%20trying%20products%2C%20sleek%20technology%20retail%20environment&width=500&height=350&seq=nbbus204&orientation=landscape',
        icon: 'ri-smartphone-line',
        rating: 4.6,
        reviews: 987,
        priceRange: '$$$',
        hours: 'Mon-Sat 10AM-9PM, Sun 11AM-7PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '',
        highlights: ['Genius Bar', 'Today at Apple', 'Trade-In Program', 'Personal Setup'],
      },
      {
        id: 205,
        name: 'Luxe Salon & Spa',
        type: 'Salon & Day Spa',
        category: 'services',
        description: 'Full-service luxury salon and day spa offering haircuts, color, facials, massages, manicures, and bridal packages. Award-winning stylists in a relaxing atmosphere.',
        address: '1100 Galleria Blvd Suite 150, Roseville, CA 95678',
        phone: '(916) 555-3005',
        website: 'https://luxesalonroseville.com',
        image: 'https://readdy.ai/api/search-image?query=Luxurious%20modern%20salon%20and%20spa%20interior%20with%20elegant%20styling%20stations%2C%20large%20mirrors%2C%20warm%20ambient%20lighting%2C%20marble%20countertops%2C%20fresh%20flowers%20and%20plush%20seating%2C%20upscale%20beauty%20salon%20atmosphere&width=500&height=350&seq=nbbus205&orientation=landscape',
        icon: 'ri-scissors-line',
        rating: 4.8,
        reviews: 267,
        priceRange: '$$$',
        hours: 'Tue-Sat 9AM-7PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '20% Off First Service',
        highlights: ['Bridal Packages', 'Massage Therapy', 'Hair Color', 'Nail Art'],
      },
      {
        id: 206,
        name: 'P.F. Chang\\\'s',
        type: 'Asian Restaurant',
        category: 'dining',
        description: 'Contemporary Asian cuisine featuring wok-fired dishes, sushi, and signature cocktails. Stylish dining room with an open kitchen and vibrant atmosphere.',
        address: '1180 Galleria Blvd, Roseville, CA 95678',
        phone: '(916) 555-3006',
        website: 'https://pfchangs.com',
        image: 'https://readdy.ai/api/search-image?query=Stylish%20Asian%20restaurant%20interior%20with%20modern%20decor%2C%20dark%20wood%20accents%2C%20dramatic%20lighting%2C%20open%20kitchen%20with%20wok%20flames%20visible%2C%20elegant%20table%20settings%2C%20contemporary%20dining%20atmosphere%20with%20Asian%20art%20on%20walls&width=500&height=350&seq=nbbus206&orientation=landscape',
        icon: 'ri-restaurant-line',
        rating: 4.2,
        reviews: 1456,
        priceRange: '$$',
        hours: 'Mon-Sun 11AM-10PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '',
        highlights: ['Wok-Fired Dishes', 'Sushi Bar', 'Gluten-Free Menu', 'Cocktails'],
      },
    ],
  },
  {
    id: 3,
    slug: 'creekside-town-center',
    name: 'Creekside Town Center',
    tagline: 'Family-Friendly Hub',
    description: 'A vibrant mixed-use center in West Roseville with family restaurants, fitness studios, medical offices, and everyday essentials surrounded by new residential communities.',
    longDescription: 'Creekside Town Center is the go-to destination for West Roseville families. This modern mixed-use development combines everyday convenience with community gathering spaces. From fitness studios and medical offices to family-friendly restaurants and specialty shops, Creekside offers everything you need within walking distance of the surrounding residential neighborhoods. The center features ample parking, pet-friendly outdoor spaces, and regular community events.',
    accentColor: '#457B9D',
    heroImage: 'https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20modern%20suburban%20town%20center%20with%20family-friendly%20restaurants%20and%20shops%2C%20outdoor%20seating%20areas%2C%20families%20walking%20on%20clean%20sidewalks%2C%20contemporary%20retail%20architecture%2C%20green%20landscaping%2C%20sunny%20California%20day%2C%20wide%20angle%20cinematic%20photography&width=1200&height=500&seq=nbdetail003&orientation=landscape',
    businessCount: 65,
    walkScore: 72,
    transitScore: 55,
    diningCount: 18,
    avgRating: 4.5,
    totalReviews: 3200,
    mapQuery: 'Creekside+Town+Center+Roseville+CA',
    highlights: ['Family Dining', 'Fitness Studios', 'Medical Offices', 'Pet Friendly', 'Community Events', 'Ample Parking'],
    categories: ['dining', 'wellness', 'services', 'shopping'],
    nearbyAttractions: [
      { name: 'West Park Community', distance: '0.3 mi', icon: 'ri-community-line' },
      { name: 'Fiddyment Farm Park', distance: '0.8 mi', icon: 'ri-tree-line' },
      { name: 'Blue Oaks Elementary', distance: '0.5 mi', icon: 'ri-school-line' },
    ],
    parkingInfo: 'Free surface parking with over 800 spaces. Covered parking available near medical offices.',
    transitInfo: 'Roseville Transit Route 7 serves Creekside Town Center with connections to downtown.',
    businesses: [
      {
        id: 301,
        name: 'Orangetheory Fitness',
        type: 'Fitness Studio',
        category: 'wellness',
        description: 'Science-backed, technology-tracked, coach-inspired group workout designed to produce results from the inside out. Heart rate monitored interval training.',
        address: '4005 Foothills Blvd Suite 100, Roseville, CA 95747',
        phone: '(916) 555-4001',
        website: 'https://orangetheory.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20fitness%20studio%20interior%20with%20treadmills%20and%20rowing%20machines%2C%20orange%20accent%20lighting%2C%20motivational%20wall%20graphics%2C%20group%20fitness%20class%20in%20session%2C%20high%20energy%20workout%20environment%20with%20mirrors%20and%20equipment&width=500&height=350&seq=nbbus301&orientation=landscape',
        icon: 'ri-heart-pulse-line',
        rating: 4.8,
        reviews: 456,
        priceRange: '$$',
        hours: 'Mon-Fri 5AM-8PM, Sat-Sun 7AM-12PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: 'First Class Free',
        highlights: ['Heart Rate Monitoring', 'Group Classes', 'Personal Coaching', 'Results Guaranteed'],
      },
      {
        id: 302,
        name: 'Panera Bread',
        type: 'Bakery & Cafe',
        category: 'dining',
        description: 'Fresh-baked bread, soups, salads, and sandwiches made with clean ingredients. Cozy cafe atmosphere with free Wi-Fi and a drive-through for convenience.',
        address: '4015 Foothills Blvd, Roseville, CA 95747',
        phone: '(916) 555-4002',
        website: 'https://panerabread.com',
        image: 'https://readdy.ai/api/search-image?query=Cozy%20bakery%20cafe%20interior%20with%20fresh%20bread%20display%2C%20comfortable%20seating%20areas%2C%20warm%20lighting%2C%20clean%20modern%20design%2C%20customers%20enjoying%20coffee%20and%20sandwiches%2C%20inviting%20casual%20dining%20atmosphere%20with%20pastry%20case&width=500&height=350&seq=nbbus302&orientation=landscape',
        icon: 'ri-cake-line',
        rating: 4.2,
        reviews: 789,
        priceRange: '$',
        hours: 'Mon-Sun 6AM-9PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '',
        highlights: ['Fresh Baked Bread', 'Free Wi-Fi', 'Drive-Through', 'Clean Ingredients'],
      },
      {
        id: 303,
        name: 'Sutter Health Clinic',
        type: 'Medical Center',
        category: 'services',
        description: 'Comprehensive primary care and urgent care clinic serving West Roseville families. Same-day appointments, pediatrics, and preventive health services.',
        address: '4025 Foothills Blvd Suite 200, Roseville, CA 95747',
        phone: '(916) 555-4003',
        website: 'https://sutterhealth.org',
        image: 'https://readdy.ai/api/search-image?query=Modern%20medical%20clinic%20waiting%20room%20with%20comfortable%20seating%2C%20clean%20white%20walls%2C%20natural%20light%20through%20large%20windows%2C%20reception%20desk%20with%20friendly%20staff%2C%20professional%20healthcare%20facility%20with%20calming%20decor&width=500&height=350&seq=nbbus303&orientation=landscape',
        icon: 'ri-hospital-line',
        rating: 4.4,
        reviews: 345,
        priceRange: '$$',
        hours: 'Mon-Fri 8AM-6PM, Sat 9AM-1PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: '',
        highlights: ['Same-Day Appointments', 'Pediatrics', 'Urgent Care', 'Telehealth'],
      },
      {
        id: 304,
        name: 'Nothing Bundt Cakes',
        type: 'Bakery',
        category: 'dining',
        description: 'Specialty bakery famous for its signature bundt cakes in a variety of flavors. Perfect for celebrations, gifts, or an everyday treat. Custom decorating available.',
        address: '4035 Foothills Blvd, Roseville, CA 95747',
        phone: '(916) 555-4004',
        website: 'https://nothingbundtcakes.com',
        image: 'https://readdy.ai/api/search-image?query=Charming%20bakery%20interior%20with%20beautifully%20decorated%20bundt%20cakes%20on%20display%2C%20pastel%20color%20scheme%2C%20clean%20glass%20display%20cases%2C%20warm%20inviting%20atmosphere%2C%20celebration%20cakes%20and%20gift%20boxes%20arranged%20neatly&width=500&height=350&seq=nbbus304&orientation=landscape',
        icon: 'ri-cake-2-line',
        rating: 4.9,
        reviews: 567,
        priceRange: '$',
        hours: 'Mon-Sat 9AM-7PM, Sun 10AM-4PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: 'Free Bundtlet with Purchase',
        highlights: ['Custom Decorating', 'Gift Bundles', 'Party Platters', 'Seasonal Flavors'],
      },
      {
        id: 305,
        name: 'West Roseville Veterinary',
        type: 'Veterinary Clinic',
        category: 'services',
        description: 'Full-service veterinary hospital providing compassionate care for dogs, cats, and exotic pets. Wellness exams, surgery, dental care, and emergency services.',
        address: '4045 Foothills Blvd Suite 300, Roseville, CA 95747',
        phone: '(916) 555-4005',
        website: 'https://westrosevet.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20veterinary%20clinic%20interior%20with%20clean%20examination%20room%2C%20friendly%20veterinarian%20examining%20a%20happy%20golden%20retriever%2C%20medical%20equipment%2C%20bright%20cheerful%20decor%20with%20pet%20photos%20on%20walls%2C%20welcoming%20animal%20hospital%20atmosphere%20with%20teal%20accents&width=500&height=350&seq=nbbus305&orientation=landscape',
        icon: 'ri-bear-smile-line',
        rating: 4.7,
        reviews: 234,
        priceRange: '$$',
        hours: 'Mon-Fri 7AM-7PM, Sat 8AM-4PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: 'Free First Exam',
        highlights: ['Emergency Services', 'Dental Care', 'Exotic Pets', 'Online Booking'],
      },
    ],
  },
  {
    id: 4,
    slug: 'douglas-boulevard',
    name: 'Douglas Boulevard Corridor',
    tagline: 'Professional Services Strip',
    description: 'Roseville\\\'s main commercial artery connecting Granite Bay to downtown, lined with professional offices, specialty restaurants, auto dealerships, and service businesses.',
    longDescription: 'Douglas Boulevard is Roseville\\\'s primary commercial corridor, stretching from the Granite Bay border through the heart of the city. This bustling thoroughfare is home to a diverse mix of professional offices, specialty restaurants, auto dealerships, banking centers, and service businesses. With mature oak trees lining the boulevard and easy access from Interstate 80, Douglas Blvd is where Roseville residents come for professional services, specialty dining, and automotive needs.',
    accentColor: '#6D597A',
    heroImage: 'https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20wide%20commercial%20boulevard%20with%20professional%20office%20buildings%2C%20specialty%20restaurants%2C%20mature%20oak%20trees%20lining%20the%20road%2C%20clean%20modern%20signage%2C%20Northern%20California%20suburban%20commercial%20district%2C%20warm%20afternoon%20light%2C%20wide%20angle%20cinematic%20street%20photography&width=1200&height=500&seq=nbdetail004&orientation=landscape',
    businessCount: 120,
    walkScore: 58,
    transitScore: 48,
    diningCount: 28,
    avgRating: 4.5,
    totalReviews: 5600,
    mapQuery: 'Douglas+Boulevard+Roseville+CA',
    highlights: ['Auto Mile', 'Professional Offices', 'Specialty Dining', 'Banking', 'Medical Centers', 'Easy I-80 Access'],
    categories: ['dining', 'services', 'shopping'],
    nearbyAttractions: [
      { name: 'Maidu Regional Park', distance: '0.4 mi', icon: 'ri-tree-line' },
      { name: 'Roseville Sports Center', distance: '0.6 mi', icon: 'ri-basketball-line' },
      { name: 'I-80 Freeway Access', distance: '0.2 mi', icon: 'ri-road-map-line' },
    ],
    parkingInfo: 'Ample free parking at all businesses along the corridor. Street parking available on side streets.',
    transitInfo: 'Roseville Transit Route 4 runs the length of Douglas Blvd with frequent stops.',
    businesses: [
      {
        id: 401,
        name: 'LaBou Bakery Cafe',
        type: 'Bakery & Cafe',
        category: 'dining',
        description: 'Beloved local bakery chain serving fresh pastries, artisan sandwiches, hearty soups, and specialty coffee. A Roseville institution since 1984.',
        address: '1500 Douglas Blvd, Roseville, CA 95661',
        phone: '(916) 555-5001',
        website: 'https://labou.com',
        image: 'https://readdy.ai/api/search-image?query=Charming%20bakery%20cafe%20with%20fresh%20pastries%20in%20glass%20display%20case%2C%20artisan%20bread%20on%20shelves%2C%20cozy%20seating%20area%20with%20warm%20lighting%2C%20coffee%20bar%20with%20espresso%20machine%2C%20welcoming%20neighborhood%20cafe%20atmosphere&width=500&height=350&seq=nbbus401&orientation=landscape',
        icon: 'ri-cup-line',
        rating: 4.5,
        reviews: 1023,
        priceRange: '$',
        hours: 'Mon-Sat 6AM-6PM, Sun 7AM-4PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: '',
        highlights: ['Fresh Pastries', 'Artisan Sandwiches', 'Specialty Coffee', 'Since 1984'],
      },
      {
        id: 402,
        name: 'Roseville Automall',
        type: 'Auto Dealerships',
        category: 'services',
        description: 'Northern California\\\'s largest auto mall featuring 15+ dealerships with new and pre-owned vehicles from all major brands. Service centers and financing available.',
        address: '200 Automall Dr, Roseville, CA 95661',
        phone: '(916) 555-5002',
        website: 'https://rosevilleautomall.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20auto%20dealership%20showroom%20with%20shiny%20new%20cars%20on%20display%2C%20clean%20polished%20floors%2C%20large%20glass%20windows%2C%20professional%20sales%20environment%2C%20multiple%20vehicle%20brands%20visible%2C%20bright%20well-lit%20automotive%20retail%20space&width=500&height=350&seq=nbbus402&orientation=landscape',
        icon: 'ri-car-line',
        rating: 4.3,
        reviews: 2156,
        priceRange: '$$$',
        hours: 'Mon-Sat 8AM-9PM, Sun 10AM-7PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: 'Special Financing Available',
        highlights: ['15+ Dealerships', 'All Major Brands', 'Service Centers', 'Financing'],
      },
      {
        id: 403,
        name: 'Sushi Omakase',
        type: 'Japanese Restaurant',
        category: 'dining',
        description: 'Authentic Japanese restaurant specializing in omakase-style sushi, fresh sashimi, and traditional Japanese dishes. Intimate setting with a master sushi chef.',
        address: '1600 Douglas Blvd, Roseville, CA 95661',
        phone: '(916) 555-5003',
        website: 'https://sushiomakase.com',
        image: 'https://readdy.ai/api/search-image?query=Elegant%20Japanese%20sushi%20restaurant%20interior%20with%20wooden%20sushi%20bar%2C%20master%20chef%20preparing%20fresh%20sushi%2C%20minimalist%20Japanese%20decor%2C%20warm%20ambient%20lighting%2C%20beautiful%20sashimi%20platter%20on%20counter%2C%20authentic%20dining%20atmosphere&width=500&height=350&seq=nbbus403&orientation=landscape',
        icon: 'ri-restaurant-line',
        rating: 4.7,
        reviews: 678,
        priceRange: '$$$',
        hours: 'Tue-Sun 11:30AM-2PM, 5PM-10PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '',
        highlights: ['Omakase Menu', 'Fresh Sashimi', 'Sake Selection', 'Private Dining'],
      },
      {
        id: 404,
        name: 'Summit Financial Advisors',
        type: 'Financial Planning',
        category: 'services',
        description: 'Independent financial planning firm helping Roseville families with retirement planning, investment management, tax strategies, and estate planning.',
        address: '1500 Eureka Rd Suite 200, Roseville, CA 95661',
        phone: '(916) 555-5004',
        website: 'https://summitfinancialroseville.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20professional%20financial%20office%20interior%20with%20elegant%20desk%20and%20comfortable%20client%20chairs%2C%20bookshelf%20with%20financial%20books%2C%20large%20windows%20with%20natural%20light%2C%20clean%20corporate%20design%20with%20warm%20neutral%20tones&width=500&height=350&seq=nbbus404&orientation=landscape',
        icon: 'ri-line-chart-line',
        rating: 4.9,
        reviews: 67,
        priceRange: '$$$',
        hours: 'Mon-Fri 9AM-5PM',
        openNow: false,
        verified: true,
        featured: false,
        offer: 'Free Consultation',
        highlights: ['Retirement Planning', 'Investment Management', 'Tax Strategy', 'Estate Planning'],
      },
      {
        id: 405,
        name: 'Roseville Family Dental',
        type: 'Dental Office',
        category: 'services',
        description: 'Comprehensive family dentistry serving Roseville for over 20 years. From routine cleanings to cosmetic procedures, gentle personalized care for all ages.',
        address: '1234 Douglas Blvd, Roseville, CA 95678',
        phone: '(916) 555-5005',
        website: 'https://rosevillefamilydental.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20dental%20office%20interior%20with%20clean%20white%20walls%20and%20teal%20accent%20chairs%2C%20professional%20healthcare%20facility%2C%20bright%20welcoming%20reception%20area%20with%20plants%20and%20natural%20light%2C%20minimalist%20medical%20office%20design&width=500&height=350&seq=nbbus405&orientation=landscape',
        icon: 'ri-tooth-line',
        rating: 4.9,
        reviews: 312,
        priceRange: '$$',
        hours: 'Mon-Fri 8AM-6PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '20% Off First Visit',
        highlights: ['Cosmetic Dentistry', 'Family Friendly', 'Emergency Care', '20+ Years'],
      },
    ],
  },
  {
    id: 5,
    slug: 'fountains-at-roseville',
    name: 'Fountains at Roseville',
    tagline: 'Outdoor Lifestyle Center',
    description: 'An open-air lifestyle center featuring popular restaurants, specialty retailers, entertainment options, and a beautiful fountain plaza perfect for evening strolls.',
    longDescription: 'The Fountains at Roseville is a premier open-air lifestyle center that combines shopping, dining, and entertainment in a beautifully landscaped setting. The centerpiece fountain plaza creates a stunning backdrop for evening strolls, while the diverse mix of restaurants, specialty retailers, and entertainment venues ensures there\\\'s something for everyone. Regular community events, seasonal celebrations, and a welcoming atmosphere make The Fountains a beloved gathering spot for Roseville residents.',
    accentColor: '#E09F3E',
    heroImage: 'https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20beautiful%20outdoor%20lifestyle%20shopping%20center%20with%20decorative%20water%20fountains%2C%20open-air%20walkways%2C%20restaurant%20patios%20with%20umbrellas%2C%20modern%20retail%20architecture%2C%20evening%20golden%20hour%20lighting%2C%20people%20enjoying%20outdoor%20dining%2C%20wide%20angle%20cinematic%20photography&width=1200&height=500&seq=nbdetail005&orientation=landscape',
    businessCount: 55,
    walkScore: 70,
    transitScore: 60,
    diningCount: 22,
    avgRating: 4.4,
    totalReviews: 4100,
    mapQuery: 'Fountains+at+Roseville+CA',
    highlights: ['Outdoor Dining', 'Fountain Plaza', 'Evening Events', 'Boutiques', 'Live Entertainment', 'Pet Friendly'],
    categories: ['dining', 'shopping', 'entertainment'],
    nearbyAttractions: [
      { name: 'Westfield Galleria', distance: '1.2 mi', icon: 'ri-shopping-bag-line' },
      { name: 'Mahany Park', distance: '0.6 mi', icon: 'ri-tree-line' },
      { name: 'Roseville Aquatics Complex', distance: '0.8 mi', icon: 'ri-water-flash-line' },
    ],
    parkingInfo: 'Free surface parking with over 1,200 spaces. Convenient drop-off zones near main entrances.',
    transitInfo: 'Roseville Transit Route 5 stops at The Fountains. Rideshare pickup zone at north entrance.',
    businesses: [
      {
        id: 501,
        name: 'BJ\\\'s Restaurant & Brewhouse',
        type: 'Restaurant & Brewhouse',
        category: 'dining',
        description: 'Casual dining restaurant known for its deep-dish pizza, craft beers brewed in-house, and extensive menu. Large patio overlooking the fountain plaza.',
        address: '1500 Eureka Rd, Roseville, CA 95661',
        phone: '(916) 555-6001',
        website: 'https://bjsrestaurants.com',
        image: 'https://readdy.ai/api/search-image?query=Upscale%20casual%20restaurant%20interior%20with%20booth%20seating%2C%20craft%20beer%20taps%20at%20bar%2C%20warm%20wood%20accents%2C%20large%20windows%20overlooking%20outdoor%20patio%2C%20deep%20dish%20pizza%20on%20table%2C%20lively%20restaurant%20atmosphere&width=500&height=350&seq=nbbus501&orientation=landscape',
        icon: 'ri-restaurant-line',
        rating: 4.3,
        reviews: 1567,
        priceRange: '$$',
        hours: 'Mon-Sun 11AM-11PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: '',
        highlights: ['Deep-Dish Pizza', 'Craft Beer', 'Fountain View Patio', 'Happy Hour'],
      },
      {
        id: 502,
        name: 'REI Co-op',
        type: 'Outdoor Gear & Apparel',
        category: 'shopping',
        description: 'Premier outdoor recreation retailer offering gear, clothing, and footwear for hiking, camping, cycling, and more. Expert staff and gear rental available.',
        address: '1530 Eureka Rd, Roseville, CA 95661',
        phone: '(916) 555-6002',
        website: 'https://rei.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20outdoor%20gear%20retail%20store%20interior%20with%20hiking%20boots%2C%20camping%20equipment%2C%20bicycles%20on%20display%2C%20organized%20merchandise%20on%20wooden%20shelves%2C%20adventure%20lifestyle%20branding%2C%20bright%20well-lit%20sporting%20goods%20store&width=500&height=350&seq=nbbus502&orientation=landscape',
        icon: 'ri-compass-3-line',
        rating: 4.7,
        reviews: 890,
        priceRange: '$$',
        hours: 'Mon-Sat 10AM-9PM, Sun 10AM-7PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: 'Member Dividend 10%',
        highlights: ['Gear Rental', 'Expert Staff', 'Bike Shop', 'Classes & Events'],
      },
      {
        id: 503,
        name: 'Yard House',
        type: 'Restaurant & Bar',
        category: 'dining',
        description: 'Upscale casual restaurant with an extensive menu and over 100 beers on tap. Great for groups, with a lively atmosphere and outdoor seating.',
        address: '1540 Eureka Rd, Roseville, CA 95661',
        phone: '(916) 555-6003',
        website: 'https://yardhouse.com',
        image: 'https://readdy.ai/api/search-image?query=Upscale%20casual%20restaurant%20and%20bar%20with%20long%20beer%20tap%20wall%2C%20modern%20industrial%20decor%2C%20comfortable%20booth%20seating%2C%20large%20screen%20TVs%2C%20vibrant%20social%20atmosphere%2C%20craft%20beer%20selection%20on%20display&width=500&height=350&seq=nbbus503&orientation=landscape',
        icon: 'ri-goblet-line',
        rating: 4.4,
        reviews: 1234,
        priceRange: '$$',
        hours: 'Mon-Sun 11AM-12AM',
        openNow: true,
        verified: true,
        featured: false,
        offer: 'Half-Price Apps Late Night',
        highlights: ['100+ Beers on Tap', 'Late Night Menu', 'Outdoor Seating', 'Group Friendly'],
      },
      {
        id: 504,
        name: 'Pottery Barn',
        type: 'Home Furnishings',
        category: 'shopping',
        description: 'Premium home furnishings store offering furniture, bedding, lighting, and decor. Free design consultations and seasonal collections for every room.',
        address: '1550 Eureka Rd, Roseville, CA 95661',
        phone: '(916) 555-6004',
        website: 'https://potterybarn.com',
        image: 'https://readdy.ai/api/search-image?query=Elegant%20home%20furnishings%20store%20interior%20with%20styled%20living%20room%20vignettes%2C%20beautiful%20furniture%20displays%2C%20warm%20lighting%2C%20decorative%20accessories%2C%20cozy%20bedroom%20setup%2C%20upscale%20home%20decor%20retail%20environment&width=500&height=350&seq=nbbus504&orientation=landscape',
        icon: 'ri-home-smile-line',
        rating: 4.5,
        reviews: 456,
        priceRange: '$$$',
        hours: 'Mon-Sat 10AM-9PM, Sun 11AM-6PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '',
        highlights: ['Free Design Consult', 'Seasonal Collections', 'Registry', 'Delivery'],
      },
    ],
  },
  {
    id: 6,
    slug: 'sierra-vista-crossing',
    name: 'Sierra Vista Crossing',
    tagline: 'Everyday Essentials',
    description: 'A convenient neighborhood center in South Roseville with grocery stores, pharmacies, quick-service restaurants, and essential services for daily living.',
    longDescription: 'Sierra Vista Crossing is South Roseville\\\'s most convenient neighborhood shopping center, designed to meet the everyday needs of local residents. Anchored by popular grocery stores and pharmacies, the center also features quick-service restaurants, coffee shops, and essential services. Its accessible location and ample parking make it the perfect stop for daily errands, quick meals, and routine shopping.',
    accentColor: '#BC4749',
    heroImage: 'https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20clean%20modern%20neighborhood%20shopping%20center%20with%20grocery%20store%2C%20pharmacy%2C%20coffee%20shops%2C%20well-maintained%20parking%20area%2C%20suburban%20California%20setting%2C%20families%20shopping%2C%20bright%20sunny%20day%2C%20contemporary%20retail%20architecture%2C%20wide%20angle%20cinematic%20photography&width=1200&height=500&seq=nbdetail006&orientation=landscape',
    businessCount: 45,
    walkScore: 65,
    transitScore: 50,
    diningCount: 14,
    avgRating: 4.4,
    totalReviews: 5200,
    mapQuery: 'Sierra+Vista+Crossing+Roseville+CA',
    highlights: ['Grocery Stores', 'Pharmacies', 'Quick Dining', 'Convenience', 'Ample Parking', 'Daily Essentials'],
    categories: ['dining', 'shopping', 'services'],
    nearbyAttractions: [
      { name: 'Sierra Vista Park', distance: '0.3 mi', icon: 'ri-tree-line' },
      { name: 'Roseville Sports Center', distance: '0.7 mi', icon: 'ri-basketball-line' },
      { name: 'Woodcreek Oaks Blvd', distance: '0.2 mi', icon: 'ri-road-map-line' },
    ],
    parkingInfo: 'Free surface parking with over 500 spaces. Handicap accessible spots near all entrances.',
    transitInfo: 'Roseville Transit Route 6 serves Sierra Vista Crossing with connections to downtown and Galleria.',
    businesses: [
      {
        id: 601,
        name: 'Trader Joe\\\'s',
        type: 'Grocery Store',
        category: 'shopping',
        description: 'Beloved neighborhood grocery store known for its unique private-label products, affordable organic options, and friendly crew members. Weekly new product arrivals.',
        address: '1001 Sierra Vista Dr, Roseville, CA 95678',
        phone: '(916) 555-7001',
        website: 'https://traderjoes.com',
        image: 'https://readdy.ai/api/search-image?query=Bright%20cheerful%20grocery%20store%20interior%20with%20colorful%20product%20displays%2C%20fresh%20produce%20section%2C%20unique%20specialty%20foods%20on%20shelves%2C%20friendly%20shopping%20atmosphere%2C%20clean%20organized%20aisles%20with%20natural%20lighting&width=500&height=350&seq=nbbus601&orientation=landscape',
        icon: 'ri-shopping-cart-line',
        rating: 4.8,
        reviews: 2345,
        priceRange: '$',
        hours: 'Mon-Sun 8AM-9PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: '',
        highlights: ['Organic Options', 'Private Label', 'Weekly New Items', 'Affordable'],
      },
      {
        id: 602,
        name: 'Starbucks Reserve',
        type: 'Coffee Shop',
        category: 'dining',
        description: 'Premium Starbucks location featuring Reserve coffees, Clover brewing system, and an expanded food menu. Cozy seating with fireplace and outdoor patio.',
        address: '1015 Sierra Vista Dr, Roseville, CA 95678',
        phone: '(916) 555-7002',
        website: 'https://starbucks.com',
        image: 'https://readdy.ai/api/search-image?query=Premium%20coffee%20shop%20interior%20with%20modern%20design%2C%20espresso%20bar%20with%20barista%20preparing%20specialty%20drinks%2C%20comfortable%20lounge%20seating%2C%20warm%20wood%20accents%2C%20cozy%20fireplace%20area%2C%20natural%20light%20through%20large%20windows&width=500&height=350&seq=nbbus602&orientation=landscape',
        icon: 'ri-cup-line',
        rating: 4.5,
        reviews: 987,
        priceRange: '$',
        hours: 'Mon-Sun 5AM-9PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: '',
        highlights: ['Reserve Coffees', 'Clover Brewing', 'Fireplace Lounge', 'Drive-Through'],
      },
      {
        id: 603,
        name: 'CVS Pharmacy',
        type: 'Pharmacy & Convenience',
        category: 'services',
        description: 'Full-service pharmacy with prescription services, immunizations, health screenings, and a wide selection of health, beauty, and convenience products.',
        address: '1025 Sierra Vista Dr, Roseville, CA 95678',
        phone: '(916) 555-7003',
        website: 'https://cvs.com',
        image: 'https://readdy.ai/api/search-image?query=Clean%20modern%20pharmacy%20interior%20with%20organized%20product%20shelves%2C%20prescription%20counter%20in%20background%2C%20health%20and%20beauty%20products%20displayed%20neatly%2C%20bright%20fluorescent%20lighting%2C%20professional%20healthcare%20retail%20environment&width=500&height=350&seq=nbbus603&orientation=landscape',
        icon: 'ri-medicine-bottle-line',
        rating: 4.1,
        reviews: 567,
        priceRange: '$',
        hours: 'Mon-Sun 7AM-10PM, Pharmacy 9AM-9PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '',
        highlights: ['Immunizations', 'Health Screenings', 'Photo Center', 'MinuteClinic'],
      },
      {
        id: 604,
        name: 'Chipotle Mexican Grill',
        type: 'Fast Casual',
        category: 'dining',
        description: 'Popular fast-casual restaurant serving customizable burritos, bowls, tacos, and salads made with responsibly sourced ingredients. Online ordering and delivery available.',
        address: '1035 Sierra Vista Dr, Roseville, CA 95678',
        phone: '(916) 555-7004',
        website: 'https://chipotle.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20fast%20casual%20restaurant%20interior%20with%20clean%20industrial%20design%2C%20food%20preparation%20line%20visible%2C%20fresh%20ingredients%20on%20display%2C%20customers%20ordering%20at%20counter%2C%20bright%20contemporary%20dining%20space%20with%20wooden%20tables&width=500&height=350&seq=nbbus604&orientation=landscape',
        icon: 'ri-restaurant-line',
        rating: 4.2,
        reviews: 1123,
        priceRange: '$',
        hours: 'Mon-Sun 10:45AM-10PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '',
        highlights: ['Responsibly Sourced', 'Customizable', 'Online Ordering', 'Delivery'],
      },
      {
        id: 605,
        name: 'Great Clips',
        type: 'Hair Salon',
        category: 'services',
        description: 'Affordable hair salon offering haircuts for the whole family. Online check-in to skip the wait and convenient walk-in appointments available.',
        address: '1045 Sierra Vista Dr, Roseville, CA 95678',
        phone: '(916) 555-7005',
        website: 'https://greatclips.com',
        image: 'https://readdy.ai/api/search-image?query=Clean%20modern%20hair%20salon%20interior%20with%20styling%20chairs%20and%20mirrors%2C%20bright%20lighting%2C%20organized%20hair%20products%20on%20shelves%2C%20professional%20salon%20equipment%2C%20welcoming%20family-friendly%20atmosphere&width=500&height=350&seq=nbbus605&orientation=landscape',
        icon: 'ri-scissors-line',
        rating: 4.0,
        reviews: 456,
        priceRange: '$',
        hours: 'Mon-Fri 9AM-9PM, Sat 8AM-6PM, Sun 10AM-6PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '$2 Off Haircut',
        highlights: ['Online Check-In', 'Walk-Ins Welcome', 'Family Friendly', 'Affordable'],
      },
    ],
  },
  {
    id: 7,
    slug: 'east-roseville',
    name: 'East Roseville',
    tagline: 'Parks & Community Living',
    description: 'A scenic residential corridor in East Roseville featuring neighborhood parks, local eateries, craft coffee shops, and community-focused businesses along Oak Ridge and Sunrise Avenue.',
    longDescription: 'East Roseville is one of the city\\\'s most beloved residential areas, where tree-lined streets, mature oak groves, and well-maintained parks create a welcoming atmosphere for families and professionals alike. The neighborhood stretches along Sunrise Avenue and Oak Ridge Drive, offering a curated mix of locally owned restaurants, specialty coffee roasters, fitness studios, and essential services. Weekend farmers markets, community yoga in the park, and neighborhood block parties make East Roseville feel like a true small-town community within the larger city. With easy access to Folsom Lake and the American River Parkway, outdoor enthusiasts find it the perfect home base.',
    accentColor: '#D4813B',
    heroImage: 'https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20scenic%20residential%20neighborhood%20in%20Northern%20California%20with%20tree-lined%20streets%20and%20mature%20oak%20trees%2C%20local%20shops%20and%20cafes%20with%20outdoor%20seating%2C%20neighborhood%20park%20with%20green%20grass%2C%20warm%20golden%20hour%20sunlight%2C%20families%20enjoying%20community%20atmosphere%2C%20charming%20suburban%20living%2C%20wide%20angle%20cinematic%20photography&width=1200&height=500&seq=nbdetail007&orientation=landscape',
    businessCount: 72,
    walkScore: 74,
    transitScore: 58,
    diningCount: 26,
    avgRating: 4.6,
    totalReviews: 3850,
    mapQuery: 'East+Roseville+CA',
    highlights: ['Community Parks', 'Local Eateries', 'Craft Coffee', 'Family Friendly', 'Farmers Market', 'Near Folsom Lake'],
    categories: ['dining', 'wellness', 'services', 'shopping'],
    nearbyAttractions: [
      { name: 'Folsom Lake Access', distance: '2.5 mi', icon: 'ri-water-flash-line' },
      { name: 'Maidu Regional Park', distance: '0.8 mi', icon: 'ri-tree-line' },
      { name: 'Sunrise MarketPlace', distance: '1.0 mi', icon: 'ri-shopping-bag-line' },
    ],
    parkingInfo: 'Free street parking throughout the neighborhood. Most businesses offer dedicated parking lots with ample spaces.',
    transitInfo: 'Roseville Transit Route 3 and 8 serve East Roseville with connections to downtown and the Galleria. Sacramento RT light rail accessible via Sunrise Blvd.',
    businesses: [
      {
        id: 701,
        name: 'Sunrise Coffee Roasters',
        type: 'Coffee Shop & Roastery',
        category: 'dining',
        description: 'Artisan coffee roastery and cafe sourcing single-origin beans from around the world. House-roasted daily, served alongside fresh pastries and light breakfast fare in a cozy, sunlit space.',
        address: '2100 Sunrise Ave, Roseville, CA 95661',
        phone: '(916) 555-8001',
        website: 'https://sunrisecoffeeroasters.com',
        image: 'https://readdy.ai/api/search-image?query=Cozy%20artisan%20coffee%20roastery%20interior%20with%20exposed%20brick%20walls%2C%20burlap%20coffee%20bean%20sacks%2C%20copper%20espresso%20machine%2C%20warm%20Edison%20bulb%20lighting%2C%20wooden%20counter%20with%20fresh%20pastries%2C%20latte%20art%20being%20poured%2C%20inviting%20neighborhood%20cafe%20atmosphere%20with%20natural%20sunlight&width=500&height=350&seq=nbbus701&orientation=landscape',
        icon: 'ri-cup-line',
        rating: 4.8,
        reviews: 412,
        priceRange: '$',
        hours: 'Mon-Fri 6AM-6PM, Sat-Sun 7AM-5PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: 'Free Pastry with First Coffee',
        highlights: ['House Roasted', 'Single Origin', 'Fresh Pastries', 'Outdoor Patio'],
      },
      {
        id: 702,
        name: 'Oak Ridge Grill',
        type: 'American Restaurant',
        category: 'dining',
        description: 'Neighborhood grill serving classic American comfort food with a California twist. Known for hand-smashed burgers, craft beer selection, and a lively weekend brunch with live acoustic music.',
        address: '2200 Oak Ridge Dr, Roseville, CA 95661',
        phone: '(916) 555-8002',
        website: 'https://oakridgegrill.com',
        image: 'https://readdy.ai/api/search-image?query=Warm%20inviting%20American%20restaurant%20interior%20with%20rustic%20wooden%20tables%2C%20leather%20booth%20seating%2C%20craft%20beer%20taps%20at%20bar%2C%20gourmet%20burger%20on%20plate%2C%20string%20lights%20on%20outdoor%20patio%2C%20casual%20neighborhood%20dining%20atmosphere%20with%20exposed%20beam%20ceiling&width=500&height=350&seq=nbbus702&orientation=landscape',
        icon: 'ri-restaurant-line',
        rating: 4.6,
        reviews: 687,
        priceRange: '$$',
        hours: 'Mon-Thu 11AM-9PM, Fri-Sat 11AM-10PM, Sun 9AM-9PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: 'Half-Price Appetizers Mon-Wed 3-6PM',
        highlights: ['Hand-Smashed Burgers', 'Craft Beer', 'Weekend Brunch', 'Live Music'],
      },
      {
        id: 703,
        name: 'East Side Fitness',
        type: 'Gym & Fitness Studio',
        category: 'wellness',
        description: 'Full-service fitness center offering group classes, personal training, yoga, and strength training. State-of-the-art equipment in a motivating, community-driven environment.',
        address: '2150 Sunrise Ave Suite 200, Roseville, CA 95661',
        phone: '(916) 555-8003',
        website: 'https://eastsidefitness.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20fitness%20gym%20interior%20with%20clean%20equipment%2C%20free%20weights%20area%2C%20group%20fitness%20class%20in%20session%2C%20motivational%20wall%20art%2C%20large%20mirrors%2C%20bright%20natural%20lighting%20through%20windows%2C%20energetic%20workout%20atmosphere%20with%20rubber%20flooring&width=500&height=350&seq=nbbus703&orientation=landscape',
        icon: 'ri-heart-pulse-line',
        rating: 4.7,
        reviews: 298,
        priceRange: '$$',
        hours: 'Mon-Fri 5AM-10PM, Sat-Sun 6AM-8PM',
        openNow: true,
        verified: true,
        featured: true,
        offer: '7-Day Free Trial',
        highlights: ['Group Classes', 'Personal Training', 'Yoga Studio', 'Sauna'],
      },
      {
        id: 704,
        name: 'Roseville Pet Hospital',
        type: 'Veterinary Clinic',
        category: 'services',
        description: 'Award-winning veterinary hospital providing comprehensive care for dogs, cats, and small animals. Wellness exams, surgery, dental care, grooming, and 24-hour emergency services.',
        address: '2300 Oak Ridge Dr, Roseville, CA 95661',
        phone: '(916) 555-8004',
        website: 'https://rosevillepethospital.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20veterinary%20hospital%20interior%20with%20clean%20examination%20room%2C%20friendly%20veterinarian%20examining%20a%20happy%20golden%20retriever%2C%20medical%20equipment%2C%20bright%20cheerful%20decor%20with%20pet%20photos%20on%20walls%2C%20welcoming%20animal%20hospital%20atmosphere%20with%20teal%20accents&width=500&height=350&seq=nbbus704&orientation=landscape',
        icon: 'ri-bear-smile-line',
        rating: 4.9,
        reviews: 345,
        priceRange: '$$',
        hours: 'Mon-Fri 7AM-8PM, Sat 8AM-5PM, Sun Emergency Only',
        openNow: true,
        verified: true,
        featured: false,
        offer: 'Free First Wellness Exam',
        highlights: ['24hr Emergency', 'Dental Care', 'Grooming', 'Online Booking'],
      },
      {
        id: 705,
        name: 'Sakura Ramen House',
        type: 'Japanese Ramen Restaurant',
        category: 'dining',
        description: 'Authentic Japanese ramen shop serving rich tonkotsu, miso, and shoyu broths simmered for 18 hours. Handmade noodles, Japanese appetizers, and a curated sake menu.',
        address: '2180 Sunrise Ave, Roseville, CA 95661',
        phone: '(916) 555-8005',
        website: 'https://sakuraramehouse.com',
        image: 'https://readdy.ai/api/search-image?query=Authentic%20Japanese%20ramen%20restaurant%20interior%20with%20wooden%20counter%20seating%2C%20steaming%20bowls%20of%20ramen%20with%20rich%20broth%20and%20toppings%2C%20minimalist%20Japanese%20decor%2C%20warm%20ambient%20lighting%2C%20chef%20preparing%20noodles%20in%20open%20kitchen%2C%20cozy%20intimate%20dining%20atmosphere&width=500&height=350&seq=nbbus705&orientation=landscape',
        icon: 'ri-restaurant-line',
        rating: 4.7,
        reviews: 523,
        priceRange: '$$',
        hours: 'Tue-Sun 11AM-9PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '',
        highlights: ['18hr Broth', 'Handmade Noodles', 'Sake Menu', 'Vegetarian Options'],
      },
      {
        id: 706,
        name: 'The Green Thumb Garden Center',
        type: 'Garden Center & Nursery',
        category: 'shopping',
        description: 'Family-owned garden center specializing in California native plants, organic vegetables, fruit trees, and garden supplies. Free weekend workshops on sustainable gardening.',
        address: '2400 Oak Ridge Dr, Roseville, CA 95661',
        phone: '(916) 555-8006',
        website: 'https://greenthumbroseville.com',
        image: 'https://readdy.ai/api/search-image?query=Beautiful%20garden%20center%20nursery%20with%20rows%20of%20colorful%20potted%20plants%20and%20flowers%2C%20greenhouse%20with%20hanging%20baskets%2C%20organic%20vegetable%20seedlings%2C%20garden%20tools%20displayed%20neatly%2C%20warm%20natural%20sunlight%2C%20lush%20green%20plants%20everywhere%2C%20inviting%20outdoor%20shopping%20atmosphere&width=500&height=350&seq=nbbus706&orientation=landscape',
        icon: 'ri-plant-line',
        rating: 4.8,
        reviews: 267,
        priceRange: '$',
        hours: 'Mon-Sat 8AM-6PM, Sun 9AM-5PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: '10% Off Native Plants',
        highlights: ['Native Plants', 'Free Workshops', 'Organic Veggies', 'Fruit Trees'],
      },
      {
        id: 707,
        name: 'Sunrise Family Chiropractic',
        type: 'Chiropractic & Wellness',
        category: 'wellness',
        description: 'Holistic chiropractic care for the whole family. Specializing in spinal adjustments, sports injury rehabilitation, prenatal care, and pediatric chiropractic in a calming environment.',
        address: '2250 Sunrise Ave Suite 110, Roseville, CA 95661',
        phone: '(916) 555-8007',
        website: 'https://sunrisefamilychiro.com',
        image: 'https://readdy.ai/api/search-image?query=Modern%20chiropractic%20wellness%20office%20interior%20with%20clean%20treatment%20room%2C%20comfortable%20adjustment%20table%2C%20calming%20neutral%20decor%20with%20green%20plants%2C%20natural%20light%20through%20windows%2C%20professional%20healthcare%20environment%20with%20warm%20welcoming%20atmosphere&width=500&height=350&seq=nbbus707&orientation=landscape',
        icon: 'ri-hand-heart-line',
        rating: 4.8,
        reviews: 189,
        priceRange: '$$',
        hours: 'Mon-Fri 8AM-6PM, Sat 9AM-1PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: 'Free Initial Consultation',
        highlights: ['Family Care', 'Sports Rehab', 'Prenatal', 'Pediatric'],
      },
      {
        id: 708,
        name: 'East Roseville Pizza Co.',
        type: 'Pizzeria',
        category: 'dining',
        description: 'Neighborhood pizzeria crafting New York-style and wood-fired pizzas with locally sourced toppings. Family-friendly atmosphere with a game room, craft sodas, and build-your-own pizza nights.',
        address: '2350 Oak Ridge Dr, Roseville, CA 95661',
        phone: '(916) 555-8008',
        website: 'https://eastrosepizza.com',
        image: 'https://readdy.ai/api/search-image?query=Charming%20neighborhood%20pizzeria%20interior%20with%20wood-fired%20pizza%20oven%2C%20fresh%20pizza%20with%20melted%20cheese%20on%20wooden%20board%2C%20red%20and%20white%20checkered%20accents%2C%20family-friendly%20dining%20area%2C%20warm%20inviting%20atmosphere%20with%20brick%20walls%20and%20string%20lights&width=500&height=350&seq=nbbus708&orientation=landscape',
        icon: 'ri-restaurant-line',
        rating: 4.5,
        reviews: 834,
        priceRange: '$',
        hours: 'Mon-Sun 11AM-10PM',
        openNow: true,
        verified: true,
        featured: false,
        offer: 'Kids Eat Free Tuesdays',
        highlights: ['Wood-Fired', 'Game Room', 'Build Your Own', 'Delivery'],
      },
    ],
  },
];

export const getNeighborhoodBySlug = (slug: string): NeighborhoodDetail | undefined => {
  return neighborhoodDetails.find((n) => n.slug === slug);
};
